import React, { useState, useMemo } from 'react';
import { Contact, ContactFilters, ContactSortOptions } from '@/types/crm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Search, Plus, Filter, Mail, Phone, Star, TrendingUp, Users } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { useCustomers } from '@/hooks/crm';
import ContactDetail from './ContactDetail';

interface ContactListProps {
  businessId: string;
}

const ContactList: React.FC<ContactListProps> = ({ businessId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Filters
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('created_at_desc');

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Use shared customers hook
  const { customers: allCustomers, loading, error, refetch } = useCustomers({
    businessId,
    lifecycleFilter,
    sourceFilter,
    sortBy,
  });

  // Show error toast if there's an error
  React.useEffect(() => {
    if (error) {
      toast.error('Failed to load contacts', {
        description: error.message || 'Please try again later.',
      });
    }
  }, [error]);

  // Apply pagination to customers
  const contacts = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return allCustomers.slice(start, end);
  }, [allCustomers, page, pageSize]);

  // Filtered contacts based on search
  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;

    const term = searchTerm.toLowerCase();
    return contacts.filter(
      (c) =>
        c.email.toLowerCase().includes(term) ||
        c.first_name?.toLowerCase().includes(term) ||
        c.last_name?.toLowerCase().includes(term) ||
        c.full_name?.toLowerCase().includes(term)
    );
  }, [contacts, searchTerm]);

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact);
    setShowDetail(true);
  };

  const getInitials = (contact: Contact) => {
    if (contact.first_name && contact.last_name) {
      return `${contact.first_name[0]}${contact.last_name[0]}`.toUpperCase();
    }
    if (contact.first_name) {
      return contact.first_name[0].toUpperCase();
    }
    return contact.email[0].toUpperCase();
  };

  const getLifecycleBadgeVariant = (stage: string) => {
    switch (stage) {
      case 'lead':
        return 'secondary';
      case 'customer':
        return 'default';
      case 'advocate':
        return 'success';
      case 'lost':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (showDetail && selectedContact) {
    return (
      <ContactDetail
        contact={selectedContact}
        onClose={() => {
          setShowDetail(false);
          setSelectedContact(null);
          refetch(); // Refresh list after closing detail
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">All Contacts</CardTitle>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap gap-2">
            <Select value={lifecycleFilter} onValueChange={setLifecycleFilter}>
              <SelectTrigger className="w-[160px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Lifecycle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                <SelectItem value="lead">Leads</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="advocate">Advocates</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="review">Reviews</SelectItem>
                <SelectItem value="loyalty">Loyalty</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="import">Import</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at_desc">Newest First</SelectItem>
                <SelectItem value="created_at_asc">Oldest First</SelectItem>
                <SelectItem value="engagement_desc">Most Engaged</SelectItem>
                <SelectItem value="score_desc">Highest Score</SelectItem>
                <SelectItem value="email_asc">A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-muted-foreground">
            Showing {filteredContacts.length} of {contacts.length} contacts
          </div>
        </CardContent>

        {/* Contacts Table */}
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">
            Loading contacts...
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No contacts found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search or filters'
                : 'Contacts will appear here as customers leave reviews or join loyalty programs'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Lifecycle</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead>Last Engagement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleContactClick(contact)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>{getInitials(contact)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{contact.full_name}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          {contact.email}
                        </div>
                        {contact.phone && (
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getLifecycleBadgeVariant(contact.lifecycle_stage)}>
                      {contact.lifecycle_stage}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="capitalize text-sm">{contact.lead_source || 'Unknown'}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {contact.tags?.slice(0, 2).map((tag) => (
                        <Badge
                          key={tag.tag_id}
                          variant="outline"
                          style={{ borderColor: tag.tag_color }}
                          className="text-xs"
                        >
                          {tag.tag_name}
                        </Badge>
                      ))}
                      {(contact.tags?.length || 0) > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{(contact.tags?.length || 0) - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <TrendingUp className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">{contact.lead_score}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {contact.last_engagement_at
                        ? new Date(contact.last_engagement_at).toLocaleDateString('en-GB').replace(/\//g, '.')
                        : 'Never'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Pagination */}
      {filteredContacts.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {page}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={filteredContacts.length < pageSize}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactList;
